import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import {
  filterEmptyValues,
  getDepartmentOptions,
  getFamiliesOptionsBySection,
  getSectionOptionsByDepartment,
} from 'utils/helpers'
import { Button, Dropdown, StoreSelect, TextInput } from 'components/common'
import {
  FILTER_FIELD_PRICE_LABEL_REPORT,
  PRICE_LABEL_REPORT_FORM_FIELDS,
} from './utils'

const {
  BARCODE,
  NAME,
  SAP_MATERIAL_NO,
  DEPARTMENT,
  SECTION,
  FAMILY,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  STORE_ID,
} = PRICE_LABEL_REPORT_FORM_FIELDS

type Props = {
  toggleFilterForm: () => void
}

const PriceLabelReportFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_PRICE_LABEL_REPORT)

  const [resetFilter, setResetFilter] = useState(false)

  const { register, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: filters,
  })

  // will watch the change of Department and Section Fields
  const deptWatch = watch(DEPARTMENT.key)
  const sectionWatch = watch(SECTION.key)

  // Department Options
  const deptOptions = useMemo(() => getDepartmentOptions(), [])

  // Section Options
  const sectionOptions = useMemo(() => {
    const dept = deptWatch || deptOptions[0]?.value

    return getSectionOptionsByDepartment(dept)
  }, [deptWatch, deptOptions])

  // Family Options
  const familyOptions = useMemo(() => {
    const section = sectionWatch || sectionOptions[0]?.value

    return getFamiliesOptionsBySection(section)
  }, [sectionWatch, deptWatch, sectionOptions])

  // The SAP hierarchy cascades off the SAP lookup tables, so it needs its own
  // watches and option lists rather than sharing the legacy ones.
  const sapDeptWatch = watch(SAP_DEPARTMENT.key)
  const sapSectionWatch = watch(SAP_SECTION.key)

  const sapDeptOptions = useMemo(() => getDepartmentOptions(true), [])

  const sapSectionOptions = useMemo(() => {
    const dept = sapDeptWatch || sapDeptOptions[0]?.value

    return getSectionOptionsByDepartment(dept, true)
  }, [sapDeptWatch, sapDeptOptions])

  const sapFamilyOptions = useMemo(() => {
    const section = sapSectionWatch || sapSectionOptions[0]?.value

    return getFamiliesOptionsBySection(section, true)
  }, [sapSectionWatch, sapDeptWatch, sapSectionOptions])

  const onFilterApply = (data: any) => {
    // Dropdown placeholder is the string 'NONE' — the API matches these exactly
    ;[
      DEPARTMENT.key,
      SECTION.key,
      FAMILY.key,
      SAP_DEPARTMENT.key,
      SAP_SECTION.key,
      SAP_FAMILY.key,
    ].forEach((key) => {
      if (data?.[key] === 'NONE') {
        delete data[key]
      }
    })

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({
        [FILTER_FIELD_PRICE_LABEL_REPORT]: filteredData,
        page: 1,
      })
    } else {
      removeQuery(FILTER_FIELD_PRICE_LABEL_REPORT)
    }

    toggleFilterForm()
  }

  const cancelFilter = () => {
    reset(filters)
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* Barcode */}
            <TextInput
              register={register}
              name={BARCODE.key}
              label={BARCODE.label}
            />

            {/* Name */}
            <TextInput register={register} name={NAME.key} label={NAME.label} />

            {/* SAP Material Number */}
            <TextInput
              register={register}
              name={SAP_MATERIAL_NO.key}
              label={SAP_MATERIAL_NO.label}
            />

            {/* Department */}
            <Dropdown
              name={DEPARTMENT.key}
              label={DEPARTMENT.label}
              options={deptOptions}
              setValue={setValue}
              defaultValue={filters?.[DEPARTMENT.key] || resetFilter}
            />

            {/* Section */}
            <Dropdown
              name={SECTION.key}
              label={SECTION.label}
              options={sectionOptions}
              setValue={setValue}
              defaultValue={filters?.[SECTION.key] || sectionOptions?.[1]}
            />

            {/* Family */}
            <Dropdown
              name={FAMILY.key}
              label={FAMILY.label}
              options={familyOptions}
              setValue={setValue}
              defaultValue={filters?.[FAMILY.key] || familyOptions?.[1]}
            />

            {/* Sub Family */}
            <TextInput
              register={register}
              name={SUB_FAMILY.key}
              label={SUB_FAMILY.label}
            />

            {/* SAP Department */}
            <Dropdown
              name={SAP_DEPARTMENT.key}
              label={SAP_DEPARTMENT.label}
              options={sapDeptOptions}
              setValue={setValue}
              defaultValue={filters?.[SAP_DEPARTMENT.key] || resetFilter}
            />

            {/* SAP Section */}
            <Dropdown
              name={SAP_SECTION.key}
              label={SAP_SECTION.label}
              options={sapSectionOptions}
              setValue={setValue}
              defaultValue={
                filters?.[SAP_SECTION.key] || sapSectionOptions?.[1]
              }
            />

            {/* SAP Family */}
            <Dropdown
              name={SAP_FAMILY.key}
              label={SAP_FAMILY.label}
              options={sapFamilyOptions}
              setValue={setValue}
              defaultValue={filters?.[SAP_FAMILY.key] || sapFamilyOptions?.[1]}
            />

            {/* SAP Sub Family */}
            <TextInput
              register={register}
              name={SAP_SUB_FAMILY.key}
              label={SAP_SUB_FAMILY.label}
            />

            {/* Store */}
            <StoreSelect
              name={STORE_ID.key}
              label={STORE_ID.label}
              setValue={setValue}
              defaultValue={filters?.[STORE_ID.key]}
            />
          </div>

          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />

            <Button
              type="button"
              buttonText="Cancel"
              variant="outline"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default PriceLabelReportFilterForm
